import { Fragment, useState } from 'react';
import useSWR from 'swr';
import { get, ApiError } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { KpiTile } from '@/components/domain/KpiTile';
import { Skeleton } from '@/components/ui/skeleton';
import {
  csvHref,
  money,
  monthLabel,
  recentMonths,
  reportHref,
  sourceLabel,
  type BillingReport,
  type IndexRequired,
} from '@/lib/billing';

// Month-end personalization billing (plan Task 2.4). Read-only: every number
// here is computed server-side in integer cents by
// ghostfit-console/lib/personalization-billing.js, so what the screen shows and
// what the CSV invoices are the same arithmetic.
//
// Personalized orders are the billable set. Plain units are shown alongside so
// a fundraiser's decorated-vs-not split is visible at a glance.

const MONTH_CHOICES = 13; // this month + the last twelve

function Money({ cents }: { cents: number }) {
  return <span className="tabular-nums">{money(cents)}</span>;
}

// The route answers 503 {error:'index_required', indexUrl} the first time this
// collection-group query runs. Surface the link instead of a generic failure —
// creating the index IS the fix, and it is one click.
function IndexBanner({ body }: { body: IndexRequired }) {
  return (
    <div className="rounded-md border border-warning/40 bg-warning/10 p-4 text-[12px]">
      <div className="font-semibold text-warning">Firestore index required</div>
      <p className="mt-1 text-text/80">{body.message}</p>
      {body.indexUrl ? (
        <a href={body.indexUrl} target="_blank" rel="noreferrer" className="mt-2 inline-block font-medium text-primary underline">
          Create the index →
        </a>
      ) : (
        <p className="mt-2 font-mono text-[11px] text-subtle">{body.detail}</p>
      )}
    </div>
  );
}

export default function PersonalizationBilling() {
  const months = recentMonths(MONTH_CHOICES);
  const [month, setMonth] = useState(months[0]);

  const { data, error, isLoading } = useSWR<BillingReport>(reportHref(month), get);

  const indexBody =
    error instanceof ApiError && error.status === 503 && (error.body as IndexRequired)?.error === 'index_required'
      ? (error.body as IndexRequired)
      : null;

  const totals = data?.totals;

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Personalization Billing"
        subtitle="Decorated locations billed per fundraiser · month-end · charges are stamped on the order at order time"
        actions={
          <>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="rounded-[8px] border border-black/[0.08] bg-surface px-2.5 py-1.5 text-[11.5px] text-text shadow-[0_1px_1.5px_rgba(0,0,0,0.04)]"
            >
              {months.map((m) => (
                <option key={m} value={m}>{monthLabel(m)}</option>
              ))}
            </select>
            <a
              href={csvHref(month)}
              className="rounded-[8px] border border-black/[0.08] bg-surface px-3 py-1.5 text-[11.5px] font-medium text-text shadow-[0_1px_1.5px_rgba(0,0,0,0.04)] hover:bg-[#f7f7f9]"
            >
              Download CSV
            </a>
          </>
        }
      />

      {indexBody ? (
        <IndexBanner body={indexBody} />
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
            <KpiTile label="Fundraisers"        value={data?.fundraiserCount} loading={isLoading} />
            <KpiTile label="Orders"             value={data?.orderCount}      loading={isLoading} />
            <KpiTile label="Personalized units" value={totals?.personalizedUnits} loading={isLoading} />
            <KpiTile label="Locations"          value={totals?.locationCount} loading={isLoading} />
            <KpiTile label="Charge"             value={totals ? money(totals.chargeCents) : undefined} loading={isLoading} />
          </div>

          {(data?.warnings || []).length > 0 && (
            <div className="mb-4 rounded-md border border-warning/40 bg-warning/10 p-3 text-[11.5px]">
              <div className="mb-1 font-semibold text-warning">{data!.warnings.length} order(s) need a look</div>
              <ul className="list-inside list-disc space-y-0.5 text-text/80">
                {data!.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="rounded-md bg-surface shadow-card">
            {error && !indexBody ? (
              <div className="p-8 text-center text-[12px] text-danger">Couldn't load the billing report.</div>
            ) : isLoading ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-5/6" />
                <Skeleton className="h-6 w-2/3" />
              </div>
            ) : (data?.fundraisers || []).length === 0 ? (
              <div className="p-8 text-center text-[12px] text-muted">
                No orders stamped for {monthLabel(month)} yet.
              </div>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-subtle">
                    <th className="px-3 py-2 font-semibold">Fundraiser / order</th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    <th className="px-3 py-2 font-semibold">Source</th>
                    <th className="px-3 py-2 text-right font-semibold">Personalized</th>
                    <th className="px-3 py-2 text-right font-semibold">Locations</th>
                    <th className="px-3 py-2 text-right font-semibold">Plain</th>
                    <th className="px-3 py-2 text-right font-semibold">Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.fundraisers.map((f) => (
                    <Fragment key={f.submissionId}>
                      {f.orders.map((r) => (
                        <tr key={`${f.submissionId}:${r.orderId}`} className="border-b border-border/50 hover:bg-bg/50">
                          <td className="px-3 py-2">
                            <div className="font-medium">{f.fundraiserName || <span className="text-subtle">(unnamed)</span>}</div>
                            <div className="font-mono text-[10px] text-subtle">{r.orderNumber || r.orderId}</div>
                          </td>
                          <td className="px-3 py-2 text-[11.5px]">
                            {r.orderDate ? new Date(r.orderDate).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`rounded-sm px-1.5 py-0.5 text-[10.5px] ${r.via === 'none' ? 'bg-bg text-muted' : 'bg-primary/10 text-primary'}`}>
                              {sourceLabel(r.via)}
                            </span>
                            {r.skuRepairNeeded && <span className="ml-1 rounded-sm bg-danger/10 px-1.5 py-0.5 text-[10.5px] text-danger">repair</span>}
                            {r.chargeMismatch && <span className="ml-1 rounded-sm bg-warning/10 px-1.5 py-0.5 text-[10.5px] text-warning">rate drift</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.personalizedUnits}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.locationCount}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted">{r.plainUnits}</td>
                          <td className="px-3 py-2 text-right"><Money cents={r.chargeCents} /></td>
                        </tr>
                      ))}
                      <tr className="border-b border-border bg-bg/40 text-[11.5px] font-medium">
                        <td className="px-3 py-1.5" colSpan={3}>Subtotal — {f.fundraiserName || f.submissionId}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{f.totals.personalizedUnits}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{f.totals.locationCount}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{f.totals.plainUnits}</td>
                        <td className="px-3 py-1.5 text-right"><Money cents={f.totals.chargeCents} /></td>
                      </tr>
                    </Fragment>
                  ))}
                  <tr className="text-[12.5px] font-semibold">
                    <td className="px-3 py-2" colSpan={3}>Total</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totals!.personalizedUnits}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totals!.locationCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totals!.plainUnits}</td>
                    <td className="px-3 py-2 text-right"><Money cents={totals!.chargeCents} /></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
